import Decimal from "decimal.js";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  WorksheetCostCategory,
  WorksheetHeaderUnit,
  WorksheetHelpers,
  WorksheetHelpersInput,
} from "generated/graphql";
import { parseDecimalInput } from "./worksheetNumbers";
import { getMaterialRowQuantity } from "./worksheetRowQuantity";

const number = (value: Decimal.Value | null | undefined): Decimal => {
  if (value === null || value === undefined || value === "") return new Decimal(0);
  try {
    return new Decimal(value as Decimal.Value);
  } catch {
    return new Decimal(0);
  }
};

const unitInfo = (unit?: string | null): [string, Decimal] | null => {
  const normalized = (unit ?? "").toLowerCase().trim();

  if (
    ["sq ft", "sq_ft", "sqft", "square feet", "square foot", "square_foot"].includes(normalized)
  ) {
    return ["area", new Decimal(1)];
  }
  if (
    ["sq yd", "sq_yd", "sqyd", "square yards", "square yard", "square_yard"].includes(normalized)
  ) {
    return ["area", new Decimal(9)];
  }
  if (["lin ft", "lin_ft", "linear feet", "linear foot", "linear_foot"].includes(normalized)) {
    return ["length", new Decimal(1)];
  }
  if (["ton", "tons", "us_ton"].includes(normalized)) {
    return ["weight", new Decimal(2000)];
  }
  if (["pound", "pounds", "lb", "lbs"].includes(normalized)) {
    return ["weight", new Decimal(1)];
  }
  if (["cubic yd", "cubic_yd", "yards", "yard"].includes(normalized)) {
    return ["volume", new Decimal(1)];
  }
  if (normalized === "") {
    return null;
  }

  return [normalized, new Decimal(1)];
};

export const convertQuantity = (
  quantity: Decimal.Value,
  fromUnit?: string | null,
  toUnit?: string | null
): Decimal | null => {
  const from = unitInfo(fromUnit);
  const to = unitInfo(toUnit);

  if (!from || !to || from[0] !== to[0]) {
    return null;
  }

  return number(quantity).times(from[1]).div(to[1]);
};

export type HelperResolution = {
  hours: Decimal | null;
  days: Decimal | null;
  travelHours: Decimal | null;
  rowQuantities: Record<string, Decimal>;
  rowTrucks: Record<string, number>;
  rowHoursPerTruck: Record<string, number>;
};

const emptyResolution = (): HelperResolution => ({
  hours: null,
  days: null,
  travelHours: null,
  rowQuantities: {},
  rowTrucks: {},
  rowHoursPerTruck: {},
});

const calculateTruckCount = (
  capacity: number,
  amount: number,
  loads: number,
  productionRate: number | null,
  totalTime: number,
  maxTripsPerDay: number | null,
  jobDays: number | null
): number => {
  if (capacity > amount) {
    return 1;
  }

  if (productionRate !== null && productionRate > 0) {
    const number = Math.round((productionRate / capacity) * totalTime * 10) / 10;
    return number > loads ? loads : number;
  }

  if (maxTripsPerDay) {
    let quantity = Math.round((loads / maxTripsPerDay) * 10) / 10;
    if (jobDays && jobDays > 1) {
      quantity = Math.round((quantity / jobDays) * 10) / 10;
    }
    return quantity;
  }

  return 1;
};

export const resolveHelpers = (item: WorksheetItemInput): HelperResolution => {
  const helpers = item.helpers;

  if (!helpers) {
    return emptyResolution();
  }

  const rows = item.costRows ?? [];
  const hoursPerDay = number(helpers.hoursPerDay || 8).gt(0)
    ? number(helpers.hoursPerDay || 8)
    : new Decimal(8);

  let hours: Decimal | null = null;
  let days: Decimal | null = null;

  const production = helpers.production;
  if (production?.enabled) {
    const helperUnit = production.unit || item.unit;
    let quantity: Decimal | null = null;

    if ((production.source ?? "item") === "material") {
      const materialRow = rows.find((row) => row.rowUuid === production.materialRowUuid);

      if (materialRow) {
        quantity = convertQuantity(
          getMaterialRowQuantity(
            materialRow,
            item.sellingQuantity,
            item.unit as WorksheetHeaderUnit
          ),
          materialRow.unit,
          helperUnit
        );
      }
    } else {
      quantity = convertQuantity(item.sellingQuantity, item.unit, helperUnit);
    }

    const rate = number(production.rate);
    if (quantity !== null && rate.gt(0)) {
      hours = quantity.div(rate);
      if ((production.period ?? "hour") === "day") {
        hours = hours.times(hoursPerDay);
      }
      days = hours.gt(0)
        ? new Decimal(Math.ceil(hours.div(hoursPerDay).toNumber()))
        : new Decimal(0);
    }
  }

  let travelHours: Decimal | null = null;
  const travel = helpers.travel;
  if (travel?.enabled) {
    const visits = travel.visits !== "" ? number(travel.visits) : days;
    const roundTrip = number(travel.roundTripHours);

    if (visits !== null && roundTrip.gt(0)) {
      travelHours = visits.times(roundTrip);
    }
  }

  const rowQuantities: Record<string, Decimal> = {};
  const rowTrucks: Record<string, number> = {};
  const rowHoursPerTruck: Record<string, number> = {};

  rows.forEach((row) => {
    if (row.quantityOverridden) {
      return;
    }

    const source = row.quantitySource || "manual";

    if (source === "duration" || source === "travel" || source === "duration_travel") {
      let quantity = new Decimal(0);
      if (source.includes("duration") && hours !== null) {
        quantity = quantity.plus(hours);
      }
      if (source.includes("travel") && travelHours !== null) {
        quantity = quantity.plus(travelHours);
      }
      rowQuantities[row.rowUuid] = quantity;

      return;
    }

    if (source !== "trucking") {
      return;
    }

    resolveHauling(row, rows, item, hours, hoursPerDay, rowQuantities, rowTrucks, rowHoursPerTruck);
  });

  return { hours, days, travelHours, rowQuantities, rowTrucks, rowHoursPerTruck };
};

const resolveHauling = (
  row: WorksheetItemInput["costRows"][number],
  rows: WorksheetItemInput["costRows"],
  item: WorksheetItemInput,
  productionHours: Decimal | null,
  hoursPerDay: Decimal,
  rowQuantities: Record<string, Decimal>,
  rowTrucks: Record<string, number>,
  rowHoursPerTruck: Record<string, number>
) => {
  const capacityUnit = row.haulCapacityUnit === "Yards" ? "Yards" : "Tons";
  const capacity = parseDecimalInput(row.truckCapacity);

  if (capacity <= 0) {
    return;
  }

  const mode = row.haulQuantityMode || "";

  let amount: Decimal | null = null;

  if (mode === "manual") {
    amount = convertQuantity(row.haulQuantity, row.haulUnit || capacityUnit, capacityUnit);
  } else if (mode === "product_tons" && (row.haulUnit || "Tons") === "Tons") {
    let total = new Decimal(0);
    rows.forEach((materialRow) => {
      if (materialRow.category !== WorksheetCostCategory.MATERIAL) {
        return;
      }
      const converted = convertQuantity(
        getMaterialRowQuantity(materialRow, item.sellingQuantity, item.unit as WorksheetHeaderUnit),
        materialRow.unit,
        "Tons"
      );
      if (converted !== null) {
        total = total.plus(converted);
      }
    });
    amount = total;
  } else {
    const materialRow = rows.find((candidate) => candidate.rowUuid === row.haulMaterialRowUuid);

    if (!materialRow) {
      return;
    }

    const converted = convertQuantity(
      getMaterialRowQuantity(materialRow, item.sellingQuantity, item.unit as WorksheetHeaderUnit),
      materialRow.unit,
      capacityUnit
    );
    if (converted === null) {
      return;
    }

    amount = converted.times(parseDecimalInput(row.haulAllocationPercent)).div(100);
  }

  if (amount === null) {
    return;
  }

  const loads = new Decimal(Math.ceil(amount.div(capacity).toNumber()));
  const billing = row.truckingUnit || "Hours";
  const cycle = number(row.haulRoundTripHours)
    .plus(number(row.haulLoadHours))
    .plus(number(row.haulUnloadHours));
  let count = 0;

  if (billing === "Hours" || row.haulPlanningHours !== "") {
    const duration =
      row.haulPlanningHours !== ""
        ? number(row.haulPlanningHours)
        : productionHours ?? (mode ? hoursPerDay : null);

    if (duration !== null && duration.gt(0) && cycle.gt(0)) {
      if (amount.gt(0)) {
        count = Math.max(
          1,
          Math.ceil(
            calculateTruckCount(
              capacity,
              amount.toNumber(),
              loads.toNumber(),
              amount.div(duration).toNumber(),
              cycle.toNumber(),
              row.haulMaxTripsPerDay === "" ? null : Number(row.haulMaxTripsPerDay),
              duration.div(hoursPerDay).toNumber()
            )
          )
        );
      }
    }
  }

  let billingAmount = amount;
  if (row.haulFullLoads && billing !== "Hours") {
    billingAmount = loads.times(capacity);
  }

  if (billing === "Hours") {
    const perTruck = count > 0 ? loads.times(cycle).div(count) : new Decimal(0);
    rowQuantities[row.rowUuid] = perTruck;
    rowHoursPerTruck[row.rowUuid] = perTruck.toNumber();
    rowTrucks[row.rowUuid] = count;

    return;
  }

  rowTrucks[row.rowUuid] = count;

  if (billing === "Loads") {
    rowQuantities[row.rowUuid] = loads;
    return;
  }

  const converted = convertQuantity(billingAmount, capacityUnit, billing);
  if (converted !== null) {
    rowQuantities[row.rowUuid] = converted;
  }
};

export const mapHelpersFromApi = (
  helpers?: WorksheetHelpers | null
): WorksheetItemInput["helpers"] => ({
  calculationVersion: helpers?.calculationVersion ?? null,
  hoursPerDay: helpers?.hoursPerDay != null ? String(helpers.hoursPerDay) : "",
  production: {
    enabled: helpers?.production?.enabled ?? false,
    source: helpers?.production?.source ?? "item",
    materialRowUuid: helpers?.production?.materialRowUuid ?? "",
    quantity: helpers?.production?.quantity != null ? String(helpers.production.quantity) : "",
    unit: helpers?.production?.unit ?? "",
    rate: helpers?.production?.rate != null ? String(helpers.production.rate) : "",
    period: helpers?.production?.period ?? "hour",
  },
  travel: {
    enabled: helpers?.travel?.enabled ?? false,
    roundTripHours:
      helpers?.travel?.roundTripHours != null ? String(helpers.travel.roundTripHours) : "",
    visits: helpers?.travel?.visits != null ? String(helpers.travel.visits) : "",
  },
});

export const mapHelpersToApi = (helpers: WorksheetItemInput["helpers"]): WorksheetHelpersInput => ({
  calculationVersion: helpers.calculationVersion ?? null,
  hoursPerDay: helpers.hoursPerDay === "" ? null : parseDecimalInput(helpers.hoursPerDay),
  production: {
    enabled: helpers.production.enabled,
    source: helpers.production.source || "item",
    materialRowUuid: helpers.production.materialRowUuid || null,
    quantity:
      helpers.production.quantity === "" ? null : parseDecimalInput(helpers.production.quantity),
    unit: helpers.production.unit || null,
    rate: helpers.production.rate === "" ? null : parseDecimalInput(helpers.production.rate),
    period: helpers.production.period || "hour",
  },
  travel: {
    enabled: helpers.travel.enabled,
    roundTripHours:
      helpers.travel.roundTripHours === ""
        ? null
        : parseDecimalInput(helpers.travel.roundTripHours),
    visits: helpers.travel.visits === "" ? null : parseDecimalInput(helpers.travel.visits),
  },
});
